/**
 * Per-Meta-campaign detail route — the Meta sibling of
 * ``routes/_authenticated/_layout/campaigns/google/$campaignslug/_slug/index.tsx``.
 *
 * Renders the same sidebar (``<CampaignSidebar>``) on the left and Meta's tabbed detail panel
 * (``<MetaCampaignDetailPanel>``) on the right, so the unified ``/campaigns`` surface keeps a
 * consistent layout regardless of platform (#4173).
 *
 * Addressed by the opaque ``metaCampaignSlug`` URL token (#4659), mirroring Google's ``$campaignslug``
 * — no enumerable integer id in the URL.
 *
 * The wrapping (TabCardPanel + grid + filter pills) is duplicated from the Google slug route; we
 * accept the duplication rather than refactoring the Google route in this PR. Future work can lift
 * the shared layout into a parent route file if more platforms land.
 */

import { useEffect } from 'react';
import { useSetAtom } from 'jotai';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { Megaphone } from 'lucide-react';

import { campaignDataAtom, metaCampaignDataAtom } from '@/store';
import { CampaignSidebar, type PlatformFilter } from '@/components/campaign/campaign-sidebar';
import { useCampaignSidebarFilters } from '@/hooks/useCampaignSidebarFilters';
import { MetaCampaignDetailHeader, MetaCampaignDetailPanel } from '@/components/campaign/meta/meta-campaign-detail-panel';
import { TabCardPanel } from '@/components/glitch-ui/tab-card-panel';
import { FilterDropdown, type FilterDropdownState } from '@/components/glitch-ui/filter-dropdown';
import { PageEmptyState } from '@/components/glitch-ui/empty-state';
import { useTestMode } from '@/hooks/useTestMode';
import googleAdsLogo from '/google-ads.svg';
import metaLogo from '/meta.png';

export const Route = createFileRoute('/_authenticated/_layout/campaigns/meta/$metaCampaignSlug')({
  component: MetaCampaignPage,
});

function MetaCampaignPage() {
  const { metaCampaignSlug } = useParams({ from: '/_authenticated/_layout/campaigns/meta/$metaCampaignSlug' });
  const navigate = useNavigate();
  const isTestMode = useTestMode();
  const setMetaCampaignData = useSetAtom(metaCampaignDataAtom);
  const setCampaignData = useSetAtom(campaignDataAtom);

  // Remember this as the last-selected campaign so returning to /campaigns restores it (#4660) —
  // mirrors the Google slug route writing campaignDataAtom. Clearing campaignDataAtom marks Meta as
  // the most recent pick (the Google route clears metaCampaignDataAtom in turn), keeping exactly one
  // of the two non-null.
  useEffect(() => {
    setMetaCampaignData(metaCampaignSlug);
    setCampaignData(null);
  }, [metaCampaignSlug, setMetaCampaignData, setCampaignData]);

  // Campaign filter state persisted via user settings — the same hook Google's slug route uses
  // (#4446), replacing the hand-rolled useState trio that reset on every navigation (#6163 P2,
  // #6182). Single-value platformFilter derives from the multi-select set, Google's exact rule.
  const { filterTypes, filterStatuses, filterPlatforms, updateFilters, HIDDEN_TYPES } = useCampaignSidebarFilters();
  const platformFilter: PlatformFilter =
    filterPlatforms.size === 1 && filterPlatforms.has('google') ? 'google' :
    filterPlatforms.size === 1 && filterPlatforms.has('meta') ? 'meta' :
    'all';

  // #6257: Meta UI is gated behind test mode until Meta App Review clears (the pre-GA guard from
  // #5976, revived). Deep links land here safely; the backend keeps its own flag regardless.
  if (!isTestMode) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between py-0.5">
          <div className="flex items-center gap-3" />
        </div>
        <div className="flex-1">
          <PageEmptyState
            icon={<Megaphone className="h-12 w-12 text-gray-400" />}
            title="Meta Campaigns"
            description="The Meta Ads campaign workspace is not available for this organization yet."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between py-0.5">
        <div className="flex items-center gap-3" />
      </div>

      <div className="flex-1">
        {/* Top-of-page Meta header — full-width failure banner + post-generation approval flow,
            mirroring where Google's route places ``<CampaignApprovalFlow>`` above its 12-col layout.
            The generation-progress card is NOT here: it renders inside the content panel (col-span-9)
            so it sits in the content column like Google's generating banner, not full-width.
            Keyed by slug: the router reuses this tree on params-only navigation, and the header's
            first-observed-progress-state ref (#6163 P1-13) must reset per campaign — an unkeyed
            render carried a completed campaign's verdict into a generating one, suppressing the
            invalidations that stream its ads in (#6179). */}
        <MetaCampaignDetailHeader key={metaCampaignSlug} slug={metaCampaignSlug} />

        <div className="grid grid-cols-12 gap-1">
          {/* Left: Campaigns List Tab Card (25%) — same component the Google route uses */}
          <div className="col-span-3 sticky top-0 self-start h-[calc(100vh-120px)]">
            <TabCardPanel
              tabs={[
                { id: 'all', label: 'Campaigns', isActive: true },
                { id: 'new', label: '+ Add', onClick: () => navigate({ to: '/new/campaign' }) },
              ]}
              actions={
                <FilterDropdown
                  sections={[
                    {
                      id: 'platforms',
                      title: 'Platform',
                      options: [
                        { value: 'google', label: 'Google', icon: <img src={googleAdsLogo} alt="" className="h-4 w-4" /> },
                        { value: 'meta', label: 'Meta', icon: <img src={metaLogo} alt="" className="w-5 h-auto" /> },
                      ],
                    },
                    { id: 'statuses', title: 'Status', options: [
                      { value: 'live', label: 'Live' },
                      { value: 'draft', label: 'Draft' },
                      { value: 'paused', label: 'Paused' },
                      { value: 'archived', label: 'Archived' },
                    ]},
                    { id: 'types', title: 'Type', options: [
                      { value: 'brand', label: 'Brand' },
                      { value: 'prospecting', label: 'Prospecting' },
                      { value: 'competitor', label: 'Competitor' },
                      { value: 'hipo', label: 'High Performance' },
                      { value: 'icp', label: 'ICP' },
                      { value: 'location', label: 'Location' },
                      { value: 'external', label: 'External' },
                      { value: 'retargeting', label: 'Retargeting' },
                    ]},
                  ]}
                  selectedValues={{
                    platforms: [...filterPlatforms],
                    statuses: [...filterStatuses],
                    types: [...filterTypes].filter((t) => !HIDDEN_TYPES.includes(t)),
                  }}
                  hiddenValues={{ types: HIDDEN_TYPES }}
                  showDateRange={false}
                  popoverWidth="w-[220px]"
                  onApply={(filters: FilterDropdownState) => {
                    updateFilters(
                      new Set(filters.selections.types || []),
                      new Set(filters.selections.statuses || []),
                      new Set(filters.selections.platforms || []),
                    );
                  }}
                />
              }
              cardClassName="overflow-hidden"
              noPadding
            >
              <CampaignSidebar
                filterTypes={filterTypes}
                filterStatuses={filterStatuses}
                platformFilter={platformFilter}
              />
            </TabCardPanel>
          </div>

          {/* Right: Meta campaign detail (75%) */}
          <div className="col-span-9">
            <MetaCampaignDetailPanel slug={metaCampaignSlug} />
          </div>
        </div>
      </div>
    </div>
  );
}
